/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { CustomMessage, mapMessage } from 'src/app/models/enums/custom-message.enum';
import { SampleTransaction } from 'src/app/models/sample-transaction.model';
import { ModalService } from 'src/app/services/modal-service/modal.service';
import { OperationMode } from 'src/app/models/enums/operation-mode.enum';
import { SharedService } from 'src/app/services/shared-service/shared.service';
import { User } from 'src/app/models/user-info.model';
import { AuthService } from 'src/app/services/auth-service/auth.service';
import { UserInfo } from 'angular-oauth2-oidc';

declare let pendo: {
  initialize: (config: { visitor: { id: string; email?: string }; account: { id: string; paymentAdviceNumber: string } }) => void;
  location: {
    pathname: string;
    search: string;
    hash: string;
    setUrl: (url: string) => void;
  };
};

@Component({
  selector: 'app-payment-transaction',
  templateUrl: './payment-transaction.component.html',
  styleUrls: ['./payment-transaction.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PaymentTransactionComponent implements OnInit {
  cargoPaymentInfo?: SampleTransaction;
  modalType = '';

  inputParametersValidationResult: [boolean, Set<string>] | undefined = [true, new Set<string>()];
  user!: User;

  paymentTransactionComponent = this;
  constructor(
    private activatedRoute: ActivatedRoute,
    public modalService: ModalService,
    public sharedService: SharedService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.authService.userInfo.subscribe((user) => (this.user = user));
    if (this.user.email.length == 0) this.getUserFromSession();

    this.activatedRoute.queryParamMap.subscribe((params) => {
      this.inputParametersValidationResult = this.validateInputParametersFields(params);

      if (this.inputParametersValidationResult === null || this.inputParametersValidationResult === undefined) {
        this.inputParametersValidationResult = [true, new Set<string>()];
      }
      this.sharedService.updateParameterValid(this.inputParametersValidationResult?.[0]);
      if (this.inputParametersValidationResult?.[0] === false) this.sharedService.showMessage(mapMessage.get(CustomMessage.WarnSomething));

      this.validateParams(params);
      this.initializePendo();

      this.sharedService.updateTransactionId(this.cargoPaymentInfo?.paymentAdviceNumber ?? '');
    });
  }

  getUserFromSession() {
    if (localStorage['id_token_claims_obj'] !== undefined) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument
      const currentUser = JSON.parse(localStorage['id_token_claims_obj']) as UserInfo;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      this.user = {
        emplid: (currentUser['emplid'] as string) || 'unknown',
        email: (currentUser['email'] as string) || 'unknown',
        firstName: (currentUser['given_name'] as string) || 'unknown',
        lastName: (currentUser['family_name'] as string) || 'unknown',
      };
    }
  }

  public setPendoUrl(buttonState: string): void {
    if (typeof pendo !== 'undefined' && this.cargoPaymentInfo) {
      pendo.location.setUrl('https://www.fakeurl.com/' + buttonState);
    }
  }

  public initializePendo(): void {
    if (typeof pendo !== 'undefined' && this.cargoPaymentInfo) {
      pendo.initialize({
        visitor: {
          id: this.user.emplid || 'unknown',
          email: this.user.email || 'unknown',
        },
        account: {
          id: this.cargoPaymentInfo.airportCode?.toString() || 'guest',
          paymentAdviceNumber: this.cargoPaymentInfo.paymentAdviceNumber?.toString() || 'unknown',
        },
      });
    }
  }

  validateParams(params: ParamMap) {
    const operationMode = Object.values(OperationMode)[Object.keys(OperationMode).indexOf(String(params.get('operationMode')))];
    this.cargoPaymentInfo = new SampleTransaction(
      params.get('paymentAdviceCurrency') || 'N/A', //'USD',
      Number(params.get('paymentAdviceAmount') || '0'), //1200.5
      params.get('paymentAdviceNumber') || 'N/A', //PADFW0000000029
      params.get('awbNumber') || 'N/A', //'021-12345678'
      params.get('airportCode') || 'N/A', //SEA
      operationMode,
      (params.get('issseRequired') || 'Y').toUpperCase(),
      params.get('source') || 'Export', // Export/Import/FFINV
      params.get('signatureRequirement') || undefined, //it's always undefined, which is correct)
      params.get('customerID') || 'N/A', //123456 - it could be undefined, which is correct)
      params.get('customerName') || undefined, //it could be undefined, which is correct)
      Number(params.get('accountNumber')) || undefined, //it could be undefined, which is correct)
      (params.get('isSignatureRequired') || 'N').toUpperCase() //it could be undefined, which is correct)
    );
  }

  onContractSelected(cargoPaymentInfo: SampleTransaction) {
    this.cargoPaymentInfo = cargoPaymentInfo;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getModalType(modalType: any) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.modalType = modalType;
    this.sharedService.updateModalType(this.modalType);
    this.modalService.open();
  }

  validateInputParametersFields(params: ParamMap): [boolean, Set<string>] {
    const fieldMap = new Map<string, boolean>();
    const errorFields = new Set<string>();

    //Set all fields as mandatory as credit line (CL) and change to not mandatory depending depending of operation mode
    fieldMap.set('paymentAdviceNumber', true);
    fieldMap.set('paymentAdviceCurrency', true);
    fieldMap.set('paymentAdviceAmount', true);
    fieldMap.set('awbNumber', true);
    fieldMap.set('airportCode', true);
    fieldMap.set('operationMode', false);
    fieldMap.set('isSignatureRequired', true);
    fieldMap.set('customerID', true);
    fieldMap.set('customerName', true);
    fieldMap.set('accountNumber', true);
    fieldMap.set('issseRequired', true);
    fieldMap.set('source', true);

    const operationMode = params.get('operationMode');

    if (operationMode === null || operationMode === 'null') {
      //NoPay
      fieldMap.set('paymentAdviceNumber', false);
      fieldMap.set('paymentAdviceCurrency', false);
      fieldMap.set('paymentAdviceAmount', false);
      fieldMap.set('customerID', false);
      fieldMap.set('customerName', false);
      fieldMap.set('accountNumber', false);
    } else if (operationMode === 'CC') {
      //Credit Card
      fieldMap.set('accountNumber', false);
    } else if (operationMode === 'CH') {
      //Cash
      fieldMap.set('accountNumber', false);
    }

    if (Number(params.get('paymentAdviceAmount') || '0') == 0) {
      fieldMap.set('accountNumber', false);
    }

    for (const [key, value] of fieldMap) {
      if (value === true && (params.get(key) === null || params.get(key)?.length === 0 || params.get(key) === 'null')) {
        errorFields.add(key);
      }
    }
    return [errorFields.size === 0, errorFields];
  }
}
